"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, type MouseEvent } from "react";
import Image from "next/image";
import Link from "next/link";
import { LogIn, Menu, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { LANGUAGE_SHORT_LABELS } from "@/shared/config/i18n/config";
import { useLanguage } from "@/shared/hooks/use-language";
import { LandingLanguageSwitcher } from "./landing-language-switcher";
function smoothScroll(e: MouseEvent<HTMLAnchorElement>) {
  const href = e.currentTarget.getAttribute("href");
  if (!href?.startsWith("#")) return;
  const el = document.getElementById(href.slice(1));
  if (el) {
    e.preventDefault();
    el.scrollIntoView({
      behavior: "smooth"
    });
  }
}
export function LandingHeader() {
  const $ = _c(49);
  const {
    t
  } = useTranslation();
  const {
    language,
    languages,
    setLanguage
  } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      const onScroll = () => setScrolled(window.scrollY > 20);
      window.addEventListener("scroll", onScroll, {
        passive: true
      });
      return () => window.removeEventListener("scroll", onScroll);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useEffect(t0, t1);
  let t2;
  if ($[2] !== t) {
    t2 = t("landing.nav.platform");
    $[2] = t;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t2) {
    t3 = {
      href: "#features",
      label: t2
    };
    $[4] = t2;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t) {
    t4 = t("landing.nav.data");
    $[6] = t;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== t4) {
    t5 = {
      href: "#stats",
      label: t4
    };
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== t) {
    t6 = t("landing.nav.activity");
    $[10] = t;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== t6) {
    t7 = {
      href: "#activity",
      label: t6
    };
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t3 || $[15] !== t5 || $[16] !== t7) {
    t8 = [t3, t5, t7];
    $[14] = t3;
    $[15] = t5;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  const navLinks = t8;
  const t9 = `mx-auto flex h-14 max-w-7xl items-center justify-between rounded-2xl border px-4 transition-all duration-300 sm:px-6 ${scrolled ? "border-slate-200/60 bg-white/75 shadow-lg shadow-slate-900/5 backdrop-blur-xl" : "border-white/40 bg-white/50 backdrop-blur-md"}`;
  let t10;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <Link href="/" className="flex items-center gap-2.5"><Image src="/images/logo.svg" alt="PharmaLogic" width={32} height={32} className="size-8 rounded-lg" /><span className="text-lg font-bold tracking-tight text-slate-900">PharmaLogic</span></Link>;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== navLinks) {
    t11 = <nav className="hidden items-center gap-1 md:flex">{navLinks.map(_temp)}</nav>;
    $[19] = navLinks;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  let t12;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <LandingLanguageSwitcher />;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <LogIn className="size-3.5" />;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  let t14;
  if ($[23] !== t) {
    t14 = t("landing.nav.login");
    $[23] = t;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  let t15;
  if ($[25] !== t14) {
    t15 = <div className="hidden items-center gap-2 md:flex">{t12}<Link href="/auth/login" className="flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800">{t13}{t14}</Link></div>;
    $[25] = t14;
    $[26] = t15;
  } else {
    t15 = $[26];
  }
  let t16;
  if ($[27] !== mobileOpen) {
    t16 = () => setMobileOpen(!mobileOpen);
    $[27] = mobileOpen;
    $[28] = t16;
  } else {
    t16 = $[28];
  }
  let t17;
  if ($[29] !== mobileOpen) {
    t17 = mobileOpen ? <X className="size-5" /> : <Menu className="size-5" />;
    $[29] = mobileOpen;
    $[30] = t17;
  } else {
    t17 = $[30];
  }
  let t18;
  if ($[31] !== t16 || $[32] !== t17) {
    t18 = <button onClick={t16} className="inline-flex size-11 items-center justify-center rounded-lg text-slate-700 transition-colors hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-teal-500/40 focus-visible:outline-none md:hidden" aria-label="Toggle menu">{t17}</button>;
    $[31] = t16;
    $[32] = t17;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  let t19;
  if ($[34] !== t11 || $[35] !== t15 || $[36] !== t18 || $[37] !== t9) {
    t19 = <div className={t9}>{t10}{t11}{t15}{t18}</div>;
    $[34] = t11;
    $[35] = t15;
    $[36] = t18;
    $[37] = t9;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== language || $[40] !== languages || $[41] !== mobileOpen || $[42] !== navLinks || $[43] !== setLanguage || $[44] !== t) {
    t20 = mobileOpen ? <div className="mx-auto mt-2 max-w-7xl rounded-2xl border border-slate-200/60 bg-white/95 px-4 py-4 shadow-lg shadow-slate-900/5 backdrop-blur-xl md:hidden"><nav className="flex flex-col gap-1">{navLinks.map(link_0 => <a key={link_0.href} href={link_0.href} onClick={e => {
          smoothScroll(e);
          setMobileOpen(false);
        }} className="rounded-lg px-4 py-2.5 text-sm text-slate-600 transition-colors hover:bg-slate-50">{link_0.label}</a>)}<div className="my-2 h-px bg-slate-100" /><div className="flex flex-wrap gap-1.5 px-4 py-2">{languages.map(lang => <button key={lang} onClick={() => void setLanguage(lang)} className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${lang === language ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}>{LANGUAGE_SHORT_LABELS[lang]}</button>)}</div><Link href="/auth/login" className="mt-2 flex items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-2.5 text-sm font-medium text-white" onClick={() => setMobileOpen(false)}><LogIn className="size-3.5" />{t("landing.nav.login")}</Link></nav></div> : null;
    $[39] = language;
    $[40] = languages;
    $[41] = mobileOpen;
    $[42] = navLinks;
    $[43] = setLanguage;
    $[44] = t;
    $[45] = t20;
  } else {
    t20 = $[45];
  }
  let t21;
  if ($[46] !== t19 || $[47] !== t20) {
    t21 = <header className="sticky top-0 z-50 px-4 pt-4">{t19}{t20}</header>;
    $[46] = t19;
    $[47] = t20;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  return t21;
}
function _temp(link) {
  return <a key={link.href} href={link.href} onClick={smoothScroll} className="rounded-lg px-3.5 py-2 text-sm text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-900">{link.label}</a>;
}